// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Box, Button, Checkbox, Group, Loader, Modal, Paper, Stack, Text, Title } from '@mantine/core';
import type { MedplumClient } from '@medplum/core';
import type { DocumentReference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconFilePlus } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { showErrorNotification } from '../../utils/notifications';
import { withTenantScope } from '../../utils/tenant';
import classes from './ClaimAttachmentsSection.module.css';

interface ClaimAttachmentsSectionProps {
  claimId: string;
  patientId: string;
}

async function openAttachment(url: string | undefined, medplum: MedplumClient): Promise<void> {
  if (!url) {
    return;
  }
  try {
    const blob = await medplum.download(url);
    const objectUrl = URL.createObjectURL(blob);
    window.open(objectUrl, '_blank');
  } catch {
    window.open(url, '_blank');
  }
}

export function ClaimAttachmentsSection({ claimId, patientId }: ClaimAttachmentsSectionProps): JSX.Element {
  const medplum = useMedplum();
  const [docs, setDocs] = useState<DocumentReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState<string>();
  const [chartDocs, setChartDocs] = useState<DocumentReference[]>([]);
  const [selectedChartDocs, setSelectedChartDocs] = useState<string[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [chartPickerOpen, setChartPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    medplum
      .searchResources('DocumentReference', { subject: `Patient/${patientId}`, related: `Claim/${claimId}` })
      .then(setDocs)
      .catch(showErrorNotification)
      .finally(() => setLoading(false));
  }, [medplum, claimId, patientId]);

  const handleRemove = async (doc: DocumentReference): Promise<void> => {
    if (!doc.id) {
      return;
    }
    setRemovingId(doc.id);
    try {
      // Keep the DocumentReference and its Binary intact; removing an attachment
      // from a claim only removes this claim from the document's related links.
      const updated: DocumentReference = {
        ...doc,
        context: {
          ...doc.context,
          related: doc.context?.related?.filter((related) => related.reference !== `Claim/${claimId}`),
        },
      };
      await medplum.updateResource(updated);
      setDocs((prev) => prev.filter((item) => item.id !== doc.id));
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setRemovingId(undefined);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      const binary = await medplum.createBinary(file, file.name, file.type);
      const doc = withTenantScope<DocumentReference>({
        resourceType: 'DocumentReference',
        status: 'current',
        subject: { reference: `Patient/${patientId}` },
        context: { related: [{ reference: `Claim/${claimId}` }] },
        content: [{ attachment: { title: file.name, url: `Binary/${binary.id}`, contentType: file.type } }],
      });
      const created = await medplum.createResource(doc);
      setDocs((prev) => [...prev, created]);
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openChartPicker = async (): Promise<void> => {
    setChartPickerOpen(true);
    setChartLoading(true);
    try {
      const resources = await medplum.searchResources('DocumentReference', { subject: `Patient/${patientId}` });
      const linkedIds = new Set(docs.map((doc) => doc.id));
      setChartDocs(resources.filter((doc) => doc.id && !linkedIds.has(doc.id)));
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setChartLoading(false);
    }
  };

  const attachSelectedChartDocs = async (): Promise<void> => {
    const selected = chartDocs.filter((doc) => doc.id && selectedChartDocs.includes(doc.id));
    if (selected.length === 0) {
      return;
    }
    setAttaching(true);
    try {
      const updatedDocs = await Promise.all(
        selected.map((doc) =>
          medplum.updateResource({
            ...doc,
            context: {
              ...doc.context,
              related: [...(doc.context?.related ?? []), { reference: `Claim/${claimId}` }],
            },
          })
        )
      );
      setDocs((prev) => [...prev, ...updatedDocs]);
      setSelectedChartDocs([]);
      setChartPickerOpen(false);
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setAttaching(false);
    }
  };

  const chartPickerContent = (): JSX.Element => {
    if (chartLoading) {
      return <Loader size="sm" />;
    }
    if (chartDocs.length === 0) {
      return (
        <Text c="dimmed" size="sm">
          No unattached documents are available in this patient chart.
        </Text>
      );
    }
    return (
      <Stack gap="xs">
        {chartDocs.map((doc) => (
          <Checkbox
            key={doc.id}
            label={doc.content?.[0]?.attachment?.title ?? 'Untitled document'}
            checked={doc.id ? selectedChartDocs.includes(doc.id) : false}
            onChange={() => {
              if (!doc.id) {
                return;
              }
              const docId = doc.id;
              setSelectedChartDocs((current) =>
                current.includes(docId) ? current.filter((id) => id !== docId) : [...current, docId]
              );
            }}
          />
        ))}
        <Group justify="flex-end" mt="sm">
          <Button onClick={attachSelectedChartDocs} loading={attaching} disabled={selectedChartDocs.length === 0}>
            Attach selected
          </Button>
        </Group>
      </Stack>
    );
  };

  return (
    <Paper className={classes.section}>
      <Title order={3} mb={4}>
        Attachments
      </Title>
      <Text className={classes.description} mb="sm">
        Supporting documentation for the payer — attach before you submit. Files are stored as DocumentReference
        resources linked to this claim.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <Stack gap="xs">
          {docs.map((doc) => {
            const att = doc.content?.[0]?.attachment;
            return (
              <Box key={doc.id} className={classes.fileRow}>
                <IconFilePlus size={15} color="#4a001f" />
                <Anchor
                  component="button"
                  type="button"
                  className={classes.fileName}
                  onClick={() => openAttachment(att?.url, medplum)}
                >
                  {att?.title ?? 'Attachment'}
                </Anchor>
                <Text className={classes.tag}>Clinical note</Text>
                <Text className={classes.fileMeta}>
                  {att?.size ? `${Math.round(att.size / 1024)} KB` : 'Linked from chart'}
                </Text>
                <button
                  className={classes.remove}
                  type="button"
                  disabled={removingId === doc.id}
                  onClick={() => handleRemove(doc)}
                >
                  {removingId === doc.id ? 'Removing…' : 'Remove'}
                </button>
              </Box>
            );
          })}
          <Box className={classes.uploadGrid}>
            <Box className={classes.uploadBox} onClick={() => fileInputRef.current?.click()}>
              <IconFilePlus size={17} color="#4a001f" />
              <Box>
                <Text className={classes.uploadTitle}>
                  {uploading ? 'Uploading…' : 'Drop files here or click to upload'}
                </Text>
                <Text className={classes.uploadHint}>PDF, JPG, PNG, or TIFF · up to 20 MB each</Text>
              </Box>
            </Box>
            <Box className={classes.uploadBox} onClick={openChartPicker}>
              <IconFilePlus size={17} color="#4a001f" />
              <Box>
                <Text className={classes.uploadTitle}>Attach from patient chart</Text>
                <Text className={classes.uploadHint}>Intake screens, HSAT report, encounter notes, RPM logs</Text>
              </Box>
            </Box>
          </Box>
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
        </Stack>
      )}
      <Modal
        opened={chartPickerOpen}
        onClose={() => setChartPickerOpen(false)}
        title="Attach from patient chart"
        centered
      >
        {chartPickerContent()}
      </Modal>
    </Paper>
  );
}
